// Dwell-debounced slide-view telemetry. A slide counts as viewed only
// after DWELL_MS of continuous visibility; rapid flipping records
// nothing. Views batch client-side and flush on an interval and on
// tab-hide (sendBeacon so the flush survives tab close). Failures are
// always silent — telemetry never breaks viewing.
import { useEffect, useRef } from 'react';

const API = '/api';
const DWELL_MS = 3000;
const FLUSH_MS = 15000;

export function useSlideViewTracker(
  deckId: string | null,
  version: string,
  slideNum: number,
) {
  const pending = useRef<Map<number, number>>(new Map());
  const dwellTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!deckId || !version || !slideNum) return;
    if (dwellTimer.current) window.clearTimeout(dwellTimer.current);
    dwellTimer.current = window.setTimeout(() => {
      pending.current.set(slideNum, (pending.current.get(slideNum) ?? 0) + 1);
    }, DWELL_MS);
    return () => {
      if (dwellTimer.current) window.clearTimeout(dwellTimer.current);
    };
  }, [deckId, version, slideNum]);

  useEffect(() => {
    if (!deckId || !version) return;
    const flush = (useBeacon: boolean) => {
      if (pending.current.size === 0) return;
      const views = [...pending.current.entries()].map(([slide_num, count]) => ({ slide_num, count }));
      pending.current.clear();
      const url = `${API}/decks/${deckId}/slide-views`;
      const payload = JSON.stringify({ version, views });
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
      } else {
        void fetch(url, {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        }).catch(() => {});
      }
    };
    const interval = window.setInterval(() => flush(false), FLUSH_MS);
    const onHide = () => { if (document.visibilityState === 'hidden') flush(true); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onHide);
      flush(true);
    };
  }, [deckId, version]);
}
